import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Heart } from 'lucide-react';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useCherishedMoments } from '@/hooks/useColorMoments';
import { ColorMomentCard } from '@/components/ColorMomentCard';
import { Skeleton } from '@/components/ui/skeleton';
import { useTransition } from '@/hooks/useTransition';

export function CherishedHues() {
  const { user } = useCurrentUser();
  const { data: moments, isLoading } = useCherishedMoments(user?.pubkey);
  const { navigateWithTransition } = useTransition();
  const backButtonRef = useRef<HTMLAnchorElement>(null);

  const handleBack = () => {
    const rect = backButtonRef.current?.getBoundingClientRect();
    navigateWithTransition('/', rect);
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-rose-200 via-amber-100 via-emerald-100 to-sky-200 dark:from-rose-950 dark:via-violet-950 dark:to-sky-950 flex flex-col items-center justify-center px-6 overflow-x-hidden">
        <div className="text-center space-y-4">
          <Heart className="h-16 w-16 mx-auto text-violet-400" />
          <h1 className="text-2xl font-semibold">cherished hues</h1>
          <p className="text-muted-foreground">Log in to save your favorite color moments</p>
          <Link
            to="/"
            className="inline-block px-6 py-3 rounded-full bg-foreground text-background font-medium hover:opacity-90 transition-opacity"
          >
            Go home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-200 via-amber-100 via-emerald-100 to-sky-200 dark:from-rose-950 dark:via-violet-950 dark:to-sky-950 overflow-x-hidden">
      {/* Decorative floating spheres - constrained to viewport */}
      <div className="absolute inset-x-0 top-0 h-64 overflow-hidden pointer-events-none" aria-hidden="true">
        {/* Violet sphere - top left, clamped to viewport */}
        <div 
          className="absolute w-32 h-32 rounded-full bg-gradient-to-br from-violet-400/60 to-violet-600/40 blur-sm"
          style={{ top: '20px', left: 'max(0px, calc(10% - 40px))' }}
        />
        {/* Rose sphere - top center-left */}
        <div 
          className="absolute w-24 h-24 rounded-full bg-gradient-to-br from-rose-400/50 to-rose-500/30 blur-sm"
          style={{ top: '60px', left: 'calc(25% - 12px)' }}
        />
        {/* Amber sphere - top center */}
        <div 
          className="absolute w-20 h-20 rounded-full bg-gradient-to-br from-amber-300/60 to-amber-500/40 blur-sm"
          style={{ top: '30px', left: 'calc(50% - 40px)' }}
        />
        {/* Sky sphere - top center-right */}
        <div 
          className="absolute w-28 h-28 rounded-full bg-gradient-to-br from-sky-400/50 to-sky-600/30 blur-sm"
          style={{ top: '50px', left: 'calc(70% - 28px)' }}
        />
        {/* Emerald sphere - top right, clamped to viewport */}
        <div 
          className="absolute w-24 h-24 rounded-full bg-gradient-to-br from-emerald-400/50 to-emerald-600/30 blur-sm"
          style={{ top: '15px', right: 'max(0px, calc(5% - 24px))' }}
        />
      </div>

      {/* Header */}
      <header className="sticky top-0 z-40 bg-background border-b border-border safe-top-mobile">
        <div className="max-w-xl mx-auto px-5 py-4 flex items-center gap-4">
          <Link
            ref={backButtonRef}
            to="/"
            onClick={(e) => {
              e.preventDefault();
              handleBack();
            }}
            className="p-2 -ml-2 rounded-full hover:bg-black/5 dark:hover:bg-white/10"
          >
            <ArrowLeft className="h-6 w-6 text-foreground" />
          </Link>
          <div className="flex items-center gap-2">
            <Heart className="h-5 w-5 text-violet-500" fill="currentColor" />
            <span className="text-lg font-medium text-foreground">cherished hues</span>
          </div>
        </div>
      </header>

      <main className="max-w-xl mx-auto px-5 pb-12 safe-bottom">
        <div className="space-y-10 pt-4">
          {isLoading ? (
            <>
              {[1, 2, 3].map((i) => (
                <div key={i} className="space-y-4">
                  <div className="aspect-square rounded-3xl overflow-hidden">
                    <Skeleton className="w-full h-full" />
                  </div>
                </div>
              ))}
            </>
          ) : moments && moments.length > 0 ? (
            moments.map((moment) => (
              <ColorMomentCard key={moment.event.id} moment={moment} />
            ))
          ) : (
            <div className="py-20 text-center space-y-6">
              <div className="w-32 h-32 mx-auto rounded-full bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center">
                <Heart className="h-12 w-12 text-violet-400" />
              </div>
              <div className="space-y-2">
                <p className="text-xl font-medium">No cherished moments yet</p>
                <p className="text-muted-foreground">
                  Tap the eye on any color moment, then bookmark it to save here
                </p>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

export default CherishedHues;
